import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { IInvoice, IInvoiceLineItem, IInvoiceTransaction } from "redux/reducers/models/order";

export interface IGetInvoice {
  id?: number;
  offset?: number;
  limit?: number;
  extended?: boolean;
  search?: string;
  financial_status?: string;
  year?: number;
}

interface IPostInvoice {
  customer_id: number;
  customer_billing_address_id: number;
  customer_shipping_address_id: number;
  date: string;
  tournament_id?: number;
  hospitality_event_id?: number;
  invoice_due: string;
  register_id?: number;
  po_number?: string;
  notes?: string;
}

interface IPutInvoice {
  invoice_id: number | string;
  customer_id: number;
  tournament_id?: number;
  hospitality_event_id?: number;
  date: string;
  invoice_due: string;
  customer_billing_address_id: number;
  customer_shipping_address_id: number;
  register_id?: number;
  notes?: string;
  po_number?: string;
}

interface IGetInvoiceLineItem {
  invoice_id: number;
}

interface IInvoiceLineItemVariant {
  variant_id: number;
  quantity: number;
  note?: string;
}

interface IInvoiceLineItemUpdate {
  id: number;
  quantity?: number;
  price?: number;
  cost?: number;
  note?: string;
}

// Send `variants` to add a whole selection in one request, or the single variant fields on their own
interface IPostInvoiceLineItem {
  invoice_id: number;
  variant_id?: number;
  quantity?: number;
  note?: string;
  variants?: IInvoiceLineItemVariant[];
}

// Send `line_items` to save several changed rows in one request, or the single line item fields on their own
interface IPutInvoiceLineItem extends Partial<IInvoiceLineItemUpdate> {
  line_items?: IInvoiceLineItemUpdate[];
}

interface IPostInvoiceTransaction {
  invoice_id?: number;
  amount: number;
  payment_option_id: number;
  kind: string;
  league_id?: number;
  tournament_id?: number;
  hospitality_event_id?: number;
  gift_card_code?: string;
  gift_card_pin?: string;
  credit_book_id?: number | string;
  account_id?: number;
  register_id?: number;
}

interface IPutCaptureInvoiceTransaction {
  payment_intent_id: string;
  transaction_id: number;
}

interface IPutCancelInvoiceTransaction {
  payment_intent_id: string | number;
  transaction_id: number;
}

interface IPutApplyDeposit {
  invoice_id: number;
  deposit_id: number;
}

interface IPutDeposit {
  deposit_id: number;
  invoice_id?: number | null;
  tournament_id?: number;
  league_id?: number;
  hospitality_event_id?: number;
}

export interface IGetInvoiceTransaction {
  offset?: number;
  limit?: number;
  invoice_id?: number;
  kind?: string;
  tournament_id?: number;
  hospitality_event_id?: number;
  league_id?: number;
  all_deposits?: boolean;
  year?: number;
}

export async function GetInvoice(params: IGetInvoice, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IInvoice[]>(APIUrl(AdminType.FacilityAdmin, "/invoice"), params, useGlobalLoader, token);
}

export async function PostInvoice(params: IPostInvoice, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice"), params, useGlobalLoader);
}

export async function PutInvoice(params: IPutInvoice, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice"), params, useGlobalLoader);
}

export async function GetInvoiceLineItem(params: IGetInvoiceLineItem, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IInvoiceLineItem[]>(
    APIUrl(AdminType.FacilityAdmin, "/invoice/line-item"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostInvoiceLineItem(params: IPostInvoiceLineItem, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/line-item"), params, useGlobalLoader);
}

export async function PutInvoiceLineItem(params: IPutInvoiceLineItem, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/line-item"), params, useGlobalLoader, token);
}

export async function DeleteInvoiceLineItem(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/invoice/line-item"), params, useGlobalLoader);
}

export async function GetDownloadInvoice(params: { invoice_id: number }, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/invoice/download"), params, useGlobalLoader);
}

interface IPutFinalizeInvoice {
  invoice_id: number;
  gift_cards: { cart_line_item_id: number; code: string; pin: number; random: boolean }[];
  register_id?: number;
  league_ids?: Array<number>;
}

export async function PutFinalizeInvoice(params: IPutFinalizeInvoice, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/finalize"), params, useGlobalLoader);
}

export async function PutVoidInvoice(params: { invoice_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/void"), params, useGlobalLoader);
}

export async function PostDuplicateInvoice(
  params: { invoice_id: number; adjustment?: boolean },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/duplicate"), params, useGlobalLoader);
}

export async function GetInvoiceTransaction(
  params: IGetInvoiceTransaction,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet<IInvoiceTransaction[]>(
    APIUrl(AdminType.FacilityAdmin, "/invoice/transaction"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostInvoiceTransaction(params: IPostInvoiceTransaction, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction"), params, useGlobalLoader);
}

export async function PutCaptureInvoiceTransaction(params: IPutCaptureInvoiceTransaction, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/capture"), params, useGlobalLoader);
}

export async function PutCancelInvoiceTransaction(params: IPutCancelInvoiceTransaction, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/cancel"), params, useGlobalLoader);
}

export async function GetInvoiceTransactionReceipt(params: { transaction_id: number }, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/receipt"), params, useGlobalLoader);
}

export async function PostInvoiceTransactionRefund(params: { transaction_id: number; amount?: number }, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/refund"), params, useGlobalLoader);
}

export async function PutApplyDeposit(params: IPutApplyDeposit, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/apply"), params, useGlobalLoader);
}

export async function PostMigrateCart(params: { cart_id?: number; invoice_id?: number }, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/migrate"), params, useGlobalLoader);
}

export async function PutDeposit(params: IPutDeposit, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/deposit"), params, useGlobalLoader);
}

export async function PostBalanceAdjustment(
  params: { invoice_id: number; new_balance: number },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/transaction/adjustment"), params, useGlobalLoader);
}

export async function PostEmailInvoice(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/invoice/email"), params, useGlobalLoader);
}
